import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { getProjectModuleAvailability } from "@/utils/projectFeatures";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/data-transfer`;
export const importError = (error) => {
  const detail = error.response?.data?.detail;
  return typeof detail === "string" ? detail : detail?.message || "The request could not be completed. Check the project list before retrying an import.";
};

// Kept under its original module name for compatibility; now an inline Settings workflow.
export default function ProjectImportDialog({ onImported = () => {}, projects = [], onBusyChange, features = {} }) {
  const modules = getProjectModuleAvailability(features);
  const [target, setTarget] = useState("projects");
  const [file, setFile] = useState(null);
  const [scan, setScan] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaults, setDefaults] = useState({});
  const [dateFormat, setDateFormat] = useState("ISO");
  const [remember, setRemember] = useState(false);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { onBusyChange?.(busy); return () => onBusyChange?.(false); }, [busy, onBusyChange]);
  const requestInFlight = useRef(false);
  const attemptId = useRef("");
  const input = useRef(null);
  const [message, setMessage] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    if ((target === 'permits' && !modules.permits) || (target === 'locates' && !modules.locates)) {
      setTarget('projects'); setFile(null); setScan(null); setReview(null); setConfirmed(false);
    }
  }, [target, modules.permits, modules.locates]);
  const invalidate = () => { setReview(null); setConfirmed(false); setMessage(""); };
  const resetFile = () => { attemptId.current = ""; setFile(null); setScan(null); setMapping({}); setDefaults({}); invalidate(); if (input.current) input.current.value = ""; };
  const chooseFile = (event) => {
    const selected = event.target.files?.[0] || null;
    // Selection starts a deliberate new attempt; review/commit retries retain
    // this identity. The server still checks all rows against current records.
    attemptId.current = selected ? Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, "0")).join("") : "";
    setFile(selected); setScan(null); invalidate();
  };
  const submit = async (step) => {
    if (!file || requestInFlight.current || (step === "commit" && (!review?.can_commit || !confirmed))) return;
    requestInFlight.current = true;
    setBusy(true);
    setMessage("");
    const form = new FormData();
    form.append("target", target);
    form.append("file", file);
    form.append("attempt_id", attemptId.current);
    form.append("config", JSON.stringify({ mapping, defaults, date_format: dateFormat, remember }));
    if (step === "commit") form.append("confirmed", "true");
    try {
      const { data } = await axios.post(`${API}/${step}`, form);
      if (step === "scan") { setScan(data); setMapping(data.mapping); setDateFormat(data.date_format); setReview(null); setConfirmed(false); }
      else if (step === "preview") { setReview(data); setConfirmed(false); }
      else {
        resetFile();
        setMessage(data.replayed ? "This file was already imported. No duplicate records were created." : `${data.imported} ${target} imported.${target === "projects" ? " They begin in Not Started." : ""}${data.warning ? ` ${data.warning}` : ""}`);
        onImported();
      }
    } catch (error) {
      setMessage(importError(error));
      setConfirmed(false);
      const errors = error.response?.data?.detail?.errors;
      setReview(errors ? { ...review, errors, can_commit: false } : null);
    } finally { requestInFlight.current = false; setBusy(false); }
  };
  return <section aria-label="Import new information" className="data-card">
    <h2>Import new information</h2>
    <p>Use your own column headings. Upload → match columns → review → import.</p>
    <p>CSV or Excel, up to 10 MB and 5,000 rows. Excel reads the active sheet. Import projects first, then their locates or permits in a separate file.</p>
    <fieldset disabled={busy}>
      <label>What are you importing?<select value={target} onChange={(event) => { setTarget(event.target.value); resetFile(); }}>
        <option value="projects">Projects</option>{modules.locates && <option value="locates">Locates / 811 tickets</option>}{modules.permits && <option value="permits">Permits</option>}
      </select></label>
      <label>Import file<input ref={input} type="file" accept=".csv,.xlsx" onChange={chooseFile} /></label>
      <button type="button" disabled={!file || busy} onClick={() => submit("scan")}>Read columns</button>
      {review?.already_imported && <p role="status">This file was already imported. Confirm to acknowledge the saved result; no duplicate records will be created.</p>}
      {scan && <>
        <h3>Match your columns</h3>
        <p>{scan.row_count} rows found. {scan.remembered ? "Your company’s saved column choices are loaded. Check them before continuing." : "Suggested matches are ready for you to check."}</p>
        <p>For unclear headings such as “Locates,” choose what the values mean. “Ignore” leaves that column out.</p>
        <div className="column-matches">{scan.columns.map((column) => <label key={column} className="column-match">
          <span><strong>{column}</strong><small>Example: {scan.sample.map((row) => row[column]).filter(Boolean).slice(0, 2).join(" · ") || "Empty"}</small></span>
          <select aria-label={`Match ${column}`} value={mapping[column] || ""} onChange={(event) => { setMapping({ ...mapping, [column]: event.target.value }); invalidate(); }}>
            <option value="">Ignore this column</option>{Object.entries(scan.fields).map(([field, label]) => <option key={field} value={field}>{label}</option>)}
          </select>
        </label>)}</div>
        <h3>Fill in missing information</h3>
        {target === "projects" ? <>
          <p>Project number, project name, customer and city are required. Notes become shared General Project Notes (up to 4,000 characters per row). Manager names must match one active project manager in your company. If omitted, JFM assigns your only active manager, or leaves the project unassigned when there are several.</p>
          {[['client', 'Customer'], ['city', 'City'], ['manager', 'Manager name']].map(([key, label]) => <label key={key}>{label} for empty cells<input value={defaults[key] || ""} onChange={(event) => { setDefaults({ ...defaults, [key]: event.target.value }); invalidate(); }} /></label>)}
        </> : <>
          <p>Match project numbers to existing open projects. Ticket or permit numbers are required. Status and dates are validated; importing a record alone does not mark a project ready.</p>
          <label>Project for empty project numbers<select value={defaults.project_code || ""} onChange={(event) => { setDefaults({ project_code: event.target.value }); invalidate(); }}>
            <option value="">Use project numbers from file</option>{projects.filter((p) => !["closed", "canceled", "deleted"].includes(p.lifecycle_state)).map((p) => <option key={p.id} value={p.project_code}>{p.project_code} · {p.name}</option>)}
          </select></label>
          <label>Date format in your file<select value={dateFormat} onChange={(event) => { setDateFormat(event.target.value); invalidate(); }}>
            <option value="ISO">YYYY-MM-DD</option><option value="MDY">MM/DD/YYYY</option><option value="DMY">DD/MM/YYYY</option>
          </select></label>
          <p>Excel date cells are recognized automatically. Dates like 03/04/2026 use the format you select.</p>
        </>}
        <label className="check-label"><input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} /> Remember these column choices for my company after a successful import</label>
        <button type="button" onClick={() => submit("preview")}>Review import</button>
      </>}
    </fieldset>
    {message && <p role="status">{message}</p>}
    {review && <section aria-label="Import review">
      <h3>Review before saving</h3>
      <p>{review.row_count} rows · {review.valid_rows} valid · {review.errors.length} need correction</p>
      {!!review.errors.length && <ul>{review.errors.map((error, index) => <li key={index}>Row {error.row_number}: {error.message}</li>)}</ul>}
      {!!review.preview?.length && <>
        <p>Sample of the first {review.preview.length} rows. Every row is validated.</p>
        <div className="data-table-scroll"><table><thead><tr>{Object.keys(review.preview[0]).map((key) => <th key={key}>{key}</th>)}</tr></thead>
          <tbody>{review.preview.map((row, index) => <tr key={index}>{Object.keys(review.preview[0]).map((key) => <td key={key}>{row[key]}</td>)}</tr>)}</tbody>
        </table></div>
      </>}
      {review.can_commit && <label className="check-label"><input type="checkbox" checked={confirmed} disabled={busy} onChange={(event) => setConfirmed(event.target.checked)} /> I reviewed this file and want to create {review.row_count} {target}.</label>}
      <button type="button" disabled={busy || !review.can_commit || !confirmed} onClick={() => submit("commit")}>{busy ? "Working…" : "Import reviewed information"}</button>
    </section>}
  </section>;
}
